/* eslint-disable import/no-cycle */
import Vue from 'vue'
import useJwt from '@/auth/jwt/useJwt'
import inactivityManager from '@/utils/inactivityManager'

const { axiosIns } = useJwt
// const baseUrl = 'https://sandbox-admin-ms.udara360.io/api'
// const baseUrl = 'https://cba-serv.udara360.io'

export default {
  namespaced: true,
  state: {
    userData: {},
    institutionData: {},
    userByUserName: '',
    expiresIn: '',
    token: null,
    refreshToken: null,
    isLoggedIn: false,
    isSessionExpired: false,
    hasSentToken: false,
    hasForcedChangeOfPassword: false,
    message: '',
  },
  getters: {
    getSession: state => state.isSessionExpired,
    getCurrentUser: state => state.userData,
    getInstitution: state => state.institutionData,
    getUserByUserName: state => state.userByUserName,
    getToken: state => state.token,
    getRefreshToken: state => state.refreshToken,
    getExpiresIn: state => state.expiresIn,
    getHasForcedChangeOfPassword: state => state.hasForcedChangeOfPassword,
    getMessage: state => state.message,
    isLoggedIn: state => state.isLoggedIn,
  },
  mutations: {
    setCurrentUser(state, data) {
      state.userData = data
    },
    setInstitution(state, data) {
      state.institutionData = data
    },
    setSession(state, isSessionExpired) {
      state.isSessionExpired = isSessionExpired
    },
    setMessage(state, message) {
      state.message = message
    },
    getUserByUserName(state, data) {
      state.userByUserName = data
    },
    setToken(state, token) {
      state.token = token
      state.isLoggedIn = !!token
    },
    setRefreshToken(state, refreshToken) {
      state.refreshToken = refreshToken
    },
    setExpiresIn(state, expiresIn) {
      state.expiresIn = expiresIn
    },
    setHasResentToken(state, hasSentToken) {
      state.hasSentToken = hasSentToken
    },
    setForcedChangeOfPassword(state, hasForcedChangeOfPassword) {
      state.hasForcedChangeOfPassword = hasForcedChangeOfPassword
    },
    setLogout(state) {
      state.token = null
      state.refreshToken = null
      state.userData = null
      state.expiresIn = null
      state.hasSentToken = null
      state.hasForcedChangeOfPassword = null
      state.userByUserName = ''
      state.isLoggedIn = false
      state.isSessionExpired = false
      state.message = ''
    },
    setUser: (state, data) => {
      // update user's permission array on refresh
      Vue.set(state.userData, 'permissions', data.permissions)
      Vue.set(state.userData, 'branch', data.branch)
      Vue.set(state.userData, 'role', data.role)
      Vue.set(state.userData, 'lastname', data.lastname)
      Vue.set(state.userData, 'firstname', data.firstname)
      Vue.set(state.userData, 'othernames', data.othernames)
      Vue.set(state.userData, 'branchCode', data?.branchCode)
      Vue.set(state.userData, 'branchID', data?.branchID)
      Vue.set(state.userData, 'phoneNumber', data?.phoneNumber)
      Vue.set(state.userData, 'address', data?.address)
      Vue.set(state.userData, 'designation', data?.designation)
      Vue.set(state.userData, 'scopeLimit', data?.scopeLimit)
      Vue.set(state.userData, 'accessLevel', data?.accessLevel)
      Vue.set(state.userData, 'isAccountOfficer', data?.isAccountOfficer)
      Vue.set(state.userData, 'enableSystemAccess', data?.enableSystemAccess)
      Vue.set(state.userData, 'enable2FA', data?.enable2FA)
      Vue.set(state.userData, 'canApprove', data?.canApprove)
      Vue.set(
        state.userData,
        'hasPermissionExclusion',
        data?.hasPermissionExclusion,
      )
    },
  },
  actions: {
    loginUser: async ({ commit }, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/generateToken',
          formData,
        )
        const { data } = await response
        commit('setToken', data.accessToken)
        commit('setRefreshToken', data.refreshToken)
        commit('setCurrentUser', data.userDetails)
        commit('setExpiresIn', data.expiresIn)
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    getInstitution: async ({ commit }, subDomain) => {
      const CACHE_KEY = 'udara360_institution_cache'
      const CACHE_TTL = 4 * 60 * 60 * 1000 // 4 hours in ms

      // Helper: apply institution data to DOM and Vuex
      const applyInstitutionData = data => {
        commit('setInstitution', data)
        if (data.preferredFont) {
          document.documentElement.style.setProperty(
            '--font-family-primary',
            `'${data.preferredFont}', sans-serif`,
            'important',
          )
        }
        if (data.primaryColor) {
          document.documentElement.style.setProperty('--primary', data.primaryColor, 'important')
          document.documentElement.style.setProperty('--secondary', data.secondaryColor, 'important')
          localStorage.setItem('themeColors', JSON.stringify({
            primaryColor: data.primaryColor,
            secondaryColor: data.secondaryColor,
          }))
        }
      }

      // Try to load from cache first
      let cached = null
      let isCacheStale = true
      try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (raw) {
          cached = JSON.parse(raw)
          // Invalidate cache if subdomain changed (different institution)
          if (cached.subDomain && cached.subDomain !== subDomain) {
            cached = null
          } else {
            isCacheStale = !cached.timestamp || (Date.now() - cached.timestamp > CACHE_TTL)
          }
        }
      } catch {
        cached = null
      }

      // Serve cached data immediately (non-blocking)
      if (cached && cached.data) {
        applyInstitutionData(cached.data)
      }

      // If cache is fresh, return early with cached data as a response-like object
      if (cached && cached.data && !isCacheStale) {
        return Promise.resolve({ data: cached.data, status: 200, cached: true })
      }

      // Fetch fresh data (blocking if no cache, background if stale cache exists)
      const fetchFresh = async () => {
        try {
          const response = await axiosIns.get(
            `/admin/api/Institution/v1/GetBasicDetailsBySubDomainName?subDomainName=${subDomain}`,
          )
          const { data } = await response

          applyInstitutionData(data)

          // Update cache with timestamp and subdomain
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
              data,
              subDomain,
              timestamp: Date.now(),
            }))
          } catch {
            // localStorage full — non-critical, continue
          }

          return response
        } catch (error) {
          // If we had cached data, swallow the error — user already has data
          if (cached && cached.data) {
            console.warn('Background institution refresh failed, using cached data:', error.message)
            return { data: cached.data, status: 200, cached: true }
          }
          throw error
        }
      }

      if (cached && cached.data) {
        // Stale cache exists — revalidate in background, don't block
        fetchFresh()
        return Promise.resolve({ data: cached.data, status: 200, cached: true })
      }

      // No cache at all — must fetch (blocking)
      try {
        return await fetchFresh()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    verifyForgotPasswordToken: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/ValidateResetPasswordToken',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    systemAccessRequest: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/SystemAccess/v1/RequestForAccess',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    authenticateUser: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/Authenticate',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    resendNewToken: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/ResendToken',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    getUserOTPCredentials: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/CreateTotpFactor',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    otpActivationAction: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/VerifyTotpFactor',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // User Forgot Password
    validateEmailAndSendToken: async (context, email) => {
      try {
        const response = await axiosIns.get(
          `/base/api/Authentication/v1/ValidateAndSendToken?username=${email}`,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    // User Reset Password
    passwordReset: async (context, data) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/generateToken',
          data,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    resendForgotPasswordNewToken: async (context, formData) => {
      try {
        const response = await axiosIns.post(
          '/base/api/Authentication/v1/ResendResetPasswordToken',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // Enforce New User Change Password with Security Question && User Change of Password
    modifyPasswordWithSecurityQuestions: async (context, formData) => {
      try {
        const response = await axiosIns.put(
          '/base/api/User/v1/ResetPasswordFlow',
          formData,
        )
        context.commit('setLogout')
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    modifySecurityDetails: async (context, formData) => {
      try {
        const response = await axiosIns.put(
          '/base/api/User/v1/changePassword',
          formData,
        )
        context.commit('setLogout')
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // Reset User Password
    changePasswordWithoutQuestion: async (context, formData) => {
      try {
        const response = await axiosIns.put(
          '/base/api/User/v1/ChangePasswordWithoutQuestion',
          formData,
        )
        context.commit('setLogout')
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // Enforce Reset User Expired Password
    changePasswordExpiration: async (context, formData) => {
      try {
        const response = await axiosIns.put(
          '/base/api/User/v1/ChangePasswordAfterExpiration',
          formData,
        )
        context.commit('setLogout')
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // get user permissions by userID
    getPermission: async ({ commit }, { permission, userId }) => {
      try {
        useJwt.setPermission(permission)
        const response = await axiosIns.get(
          `/base/api/Permission/v1/GetPermissionsByUserID?userID=${userId}`,
        )
        commit('setUser', response.data)
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // Handle Returned Question and Answer
    saveResetQuestion: async (context, formData) => {
      try {
        const response = await axiosIns.put(
          '/base/api/User/v1/ValidateSecurityQuestion',
          formData,
        )
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    updateSessionState: async ({ commit }, value) => {
      commit('setSession', value)
    },
    updateMessage: async ({ commit }, value) => {
      commit('setMessage', value)
    },
    logout: async ({ commit, state }) => {
      try {
        await axiosIns.put('/base/api/User/v1/InvalidateSession', {
          email: state.userData.email,
        })

        // Disable inactivity manager
        inactivityManager.default.disable()

        // Clear JWT tokens using useJwt methods (pass empty strings, not null)
        useJwt.setToken('')
        useJwt.setRefreshToken('')
        useJwt.setEmail('')

        // Clear Vuex state using setLogout mutation
        commit('setLogout')
        commit('operations/resetOperationState', null, { root: true })
        commit('account/resetAccountState', null, { root: true })

        // Clear localStorage and sessionStorage
        const keysToRemove = [
          '_vid_lr',
          '_vid_t',
          'accessToken',
          'email',
          'vuex',
          'refreshToken',
        ]
        keysToRemove.forEach(key => localStorage.removeItem(key))
        sessionStorage.clear()
      } catch (error) {
        useJwt.setToken('')
        useJwt.setRefreshToken('')
        useJwt.setEmail('')

        commit('setLogout')
        commit('operations/resetOperationState', null, { root: true })
        commit('account/resetAccountState', null, { root: true })
      }
    },
  },
}
